/** Collection and scope jointly isolate identities, dimension lookups and cleanup. */
export interface SemanticScope {
  collection: string;
  scopeKey: string;
}

/** One source's current preparation/generation outcome, not an index generation. */
export interface SemanticSource extends SemanticScope {
  /** Opaque persistence identity, unique within the collection/scope. */
  sourceId: string;
  /** Domain reference such as a URN; distinct from the persistence identity. */
  sourceKey: string;
  /** Ownership key for scoped bulk cleanup, independent of source identity. */
  groupKey: string;
  /** Success has candidates; empty is valid preparation; failure requires a failure code. */
  status: 'success' | 'empty' | 'failure';
  failureCode?: string;
  /** Caller-defined preparation compatibility, before observing vector dimensions. */
  preparationFingerprint?: string;
  /** Generated-vector compatibility, including dimensions; dimensions alone are insufficient. */
  compatibilityFingerprint?: string;
  /** Prepared content/evidence digest, separate from vector compatibility. */
  contentDigest?: string;
  /** Observed vector length, required for successful sources. */
  dimensions?: number;
  /** JSON-serializable backend-only evidence; storage does not interpret domain reasons. */
  diagnostics: readonly Record<string, unknown>[];
}

export interface SemanticCandidate {
  /** Stable identity unique across all sources in the same collection/scope. */
  id: string;
  normalizedContent: string;
  compatibilityFingerprint: string;
  embedding: number[];
}

export type SemanticStorageStatus =
  | { available: true }
  | { available: false; code: string; message: string };

export class SemanticStorageUnavailableError extends Error {
  readonly statusCode = 503;
  readonly code = 'semantic_storage_unavailable';

  constructor(
    readonly reason: Exclude<SemanticStorageStatus, { available: true }>,
  ) {
    super(reason.message);
    this.name = 'SemanticStorageUnavailableError';
  }
}

export interface SemanticPersistence {
  removeCollection?(collection: string, signal?: AbortSignal): Promise<void>;
  /** Deletes a group's sources and candidates only within the supplied collection/scope. */
  removeGroup(
    scope: SemanticScope,
    groupKey: string,
    signal?: AbortSignal,
  ): Promise<void>;
  /** Distinct dimensions from successful sources matching this scope and preparation definition. */
  observedDimensions(
    scope: SemanticScope,
    preparationFingerprint: string,
  ): Promise<number[]>;
  /**
   * Atomically replaces one source and its candidates; prior group cleanup is separate.
   * Success requires nonempty candidates with matching fingerprints/dimensions.
   * Empty requires a preparation fingerprint and no failure code or candidates;
   * failure requires a failure code and no candidates.
   */
  persistSource(
    source: SemanticSource,
    candidates: SemanticCandidate[],
    signal?: AbortSignal,
  ): Promise<void>;
}

/**
 * Checks an exact, nonempty batch of finite, nonzero vectors of equal positive length.
 * Does not validate model/recipe compatibility or the caller's expected dimensions.
 */
export function validSemanticVectors(
  vectors: unknown,
  count: number,
): vectors is number[][] {
  if (!Array.isArray(vectors) || vectors.length !== count || !vectors.length)
    return false;
  const dimensions = Array.isArray(vectors[0]) ? vectors[0].length : 0;
  return (
    dimensions > 0 &&
    vectors.every(
      vector =>
        Array.isArray(vector) &&
        vector.length === dimensions &&
        vector.every(
          value => typeof value === 'number' && Number.isFinite(value),
        ) &&
        vector.some(value => value !== 0),
    )
  );
}
